/**
 * §10.1 Hero — "Building the Future of AI Trading Platforms".
 * Left: title, subtitle, CTAs, trust chips.
 * Right: cinematic ecosystem visualisation — Kalki Ai Cloud core with
 * three concentric orbits of tone-tinted product modules pulsing
 * outward, evoking "single idea → global platform".
 */

import React from 'react'
import {
  Rocket, Bot, LineChart, GraduationCap, ShieldCheck, Building2, PieChart,
  CreditCard, UsersRound, BellRing, Cog, Sparkles, ArrowRight, Layers, Zap,
} from 'lucide-react'
import {
  Container, Eyebrow, GradientButton, GhostButton,
} from '@/components/landing/design-system'

// Twelve product modules arranged on two rings for the visualisation.
const INNER_MODULES = [
  { id: 'ai',       iconKey: 'Bot',           tone: '#FFC107' },
  { id: 'trading',  iconKey: 'LineChart',     tone: '#22c55e' },
  { id: 'wl',       iconKey: 'Building2',     tone: '#22d3ee' },
  { id: 'academy',  iconKey: 'GraduationCap', tone: '#a855f7' },
  { id: 'crm',      iconKey: 'UsersRound',    tone: '#3b82f6' },
  { id: 'billing',  iconKey: 'CreditCard',    tone: '#22c55e' },
]
const OUTER_MODULES = [
  { id: 'analytics', iconKey: 'PieChart',     tone: '#3b82f6' },
  { id: 'security',  iconKey: 'ShieldCheck',  tone: '#f43f5e' },
  { id: 'scale',     iconKey: 'Layers',       tone: '#22d3ee' },
  { id: 'notif',     iconKey: 'BellRing',     tone: '#f59e0b' },
  { id: 'admin',     iconKey: 'Cog',          tone: '#a855f7' },
  { id: 'launch',    iconKey: 'Rocket',       tone: '#FFC107' },
]
const ICONS = {
  Bot, LineChart, Building2, GraduationCap, UsersRound, CreditCard,
  PieChart, ShieldCheck, Layers, BellRing, Cog, Rocket, Sparkles, Zap,
}

export default function Hero() {
  const cx = 280, cy = 200, R1 = 100, R2 = 165

  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{ background: 'radial-gradient(60% 45% at 50% 20%, rgba(255,193,7,0.14), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,1)_100%)]" />

      <Container size="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Eyebrow>Company</Eyebrow>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              Building the future of{' '}
              <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">AI trading platforms.</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Kalki Ai Cloud is dedicated to empowering businesses with enterprise-grade AI trading
              infrastructure — enabling organisations to launch, operate and scale their own
              branded platforms through innovation, reliability and continuous improvement.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <GradientButton href="/platform" size="lg">
                <Sparkles className="h-4 w-4" /> Explore the platform
              </GradientButton>
              <GhostButton href="/contact" size="lg" trailingIcon>
                Contact our team
              </GhostButton>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {['Founded by operators', 'Bengaluru HQ · Global team', 'Enterprise procurement ready', 'GDPR ready'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300">
                  <Sparkles className="h-3 w-3 text-brand-gold" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — ecosystem visualisation */}
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-5 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 50% 50%, rgba(255,193,7,0.10), transparent 70%)' }} />

              <svg viewBox="0 0 560 400" className="relative w-full h-auto">
                <defs>
                  <radialGradient id="co-center" cx="50%" cy="50%" r="50%">
                    <stop offset="0%"   stopColor="#FFC107" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#FFC107" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* Orbit rails */}
                <circle cx={cx} cy={cy} r={R1} fill="none" stroke="rgba(255,255,255,0.06)" strokeDasharray="3 6" className="animate-brand-dash" />
                <circle cx={cx} cy={cy} r={R2} fill="none" stroke="rgba(255,255,255,0.05)" strokeDasharray="3 6" />

                {/* Outer modules */}
                {OUTER_MODULES.map((m, i) => {
                  const a = (i * 2 * Math.PI) / OUTER_MODULES.length - Math.PI / 2
                  const x = cx + Math.cos(a) * R2
                  const y = cy + Math.sin(a) * R2
                  const Icon = ICONS[m.iconKey] || Sparkles
                  return (
                    <g key={m.id} transform={`translate(${x} ${y})`}>
                      <circle r="18" fill="rgba(10,10,12,0.9)" stroke={m.tone} strokeOpacity="0.45" strokeWidth="1.2" />
                      <circle r="16" fill={`${m.tone}12`} />
                      <foreignObject x="-9" y="-9" width="18" height="18">
                        <div className="flex items-center justify-center w-full h-full" style={{ color: m.tone }}>
                          <Icon strokeWidth={2} style={{ width: 10, height: 10 }} />
                        </div>
                      </foreignObject>
                    </g>
                  )
                })}

                {/* Inner modules */}
                {INNER_MODULES.map((m, i) => {
                  const a = (i * 2 * Math.PI) / INNER_MODULES.length - Math.PI / 2
                  const x = cx + Math.cos(a) * R1
                  const y = cy + Math.sin(a) * R1
                  const Icon = ICONS[m.iconKey] || Sparkles
                  return (
                    <g key={m.id}>
                      {/* connector to core */}
                      <line x1={cx} y1={cy} x2={x} y2={y} stroke={`${m.tone}44`} strokeWidth="1" />
                      <g transform={`translate(${x} ${y})`}>
                        <circle r="22" fill="rgba(10,10,12,0.9)" stroke={m.tone} strokeOpacity="0.55" strokeWidth="1.3" />
                        <circle r="20" fill={`${m.tone}12`} />
                        <foreignObject x="-11" y="-11" width="22" height="22">
                          <div className="flex items-center justify-center w-full h-full" style={{ color: m.tone }}>
                            <Icon strokeWidth={1.8} style={{ width: 12, height: 12 }} />
                          </div>
                        </foreignObject>
                      </g>
                    </g>
                  )
                })}

                {/* Centre — Kalki Ai Cloud */}
                <g transform={`translate(${cx} ${cy})`}>
                  <circle r="85" fill="url(#co-center)" />
                  <circle r="46" fill="rgba(10,10,12,0.95)" stroke="rgba(255,193,7,0.45)" strokeWidth="1.6" />
                  <circle r="42" fill="rgba(255,193,7,0.09)" />
                  <foreignObject x="-20" y="-20" width="40" height="40">
                    <div className="flex items-center justify-center w-full h-full text-brand-gold">
                      <Building2 strokeWidth={1.8} style={{ width: 20, height: 20 }} />
                    </div>
                  </foreignObject>
                  <text y="66" textAnchor="middle" fontSize="11.5" fontWeight="700" fill="rgba(255,255,255,0.95)">Kalki Ai Cloud</text>
                </g>
              </svg>

              <div className="relative mt-2 flex items-center justify-between rounded-md border border-emerald-400/20 bg-emerald-400/[0.05] px-3 py-2">
                <div className="flex items-center gap-2 text-[10.5px] text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span><span className="text-white font-semibold">One platform,</span> twelve modules, one contract</span>
                </div>
                <div className="flex items-center gap-1 text-[10.5px] text-emerald-400 font-mono">
                  since 2023 <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
